import { useFlag } from '@reflag/react-sdk';

import type * as api from '@gitbook/api-client';
import type { NavigationSiteSettingsSection } from '@gitbook/app-navigation';
import { checkIsCommunityProductKey } from '@gitbook/app-pricing';
import { Icon } from '@gitbook/spine-icons';

/**
 * Definition of sections and groupings for settings in the site settings tab menu / mobile palette.
 */
export const siteSettingsSections = [
    [
        {
            type: 'section',
            id: 'general',
            title: 'General',
            icon: Icon.Settings,
        },
        {
            type: 'section',
            id: 'agent',
            title: 'Agents',
            icon: Icon.AIAgent,
        },
    ],
    [
        {
            type: 'section',
            id: 'audience',
            title: 'Audience',
            icon: Icon.PublishStatusPublic,
        },
        {
            type: 'section',
            id: 'domains',
            title: 'Domain and URL',
            icon: Icon.Globe,
        },
        {
            type: 'section',
            id: 'redirects',
            title: 'Redirects',
            icon: Icon.Redirect,
        },
    ],
    [
        {
            type: 'section',
            id: 'connections',
            title: 'Connections',
            icon: Icon.Connectors,
        },
        {
            type: 'section',
            id: 'channels',
            title: 'Channels',
            icon: Icon.Channel,
        },
        {
            type: 'section',
            id: 'embed',
            title: 'Docs Embed',
            icon: Icon.DocsEmbed,
        },
        {
            type: 'section',
            id: 'mcp',
            title: 'MCP access',
            icon: Icon.MCP,
        },
        {
            type: 'section',
            id: 'legal',
            title: 'Legal',
            icon: Icon.RoleReader,
        },
    ],
    [
        {
            type: 'section',
            id: 'sponsorship',
            title: 'Ads and sponsorship',
            icon: Icon.PaidMember,
        },
        {
            type: 'section',
            id: 'plan',
            title: 'Plan',
            icon: Icon.Upgrade,
        },
    ],
] as const;

/**
 * Return the site settings sections that should be visible for the current site.
 */
export function useVisibleSiteSettingsSections(
    site: api.Site,
    organization: api.Organization
): Record<NavigationSiteSettingsSection, boolean> {
    const isCommunity = checkIsCommunityProductKey(organization.plan);
    const isEmbedAssistantPanelEnabled = useFlag('EMBED_ASSISTANT_PANEL').isEnabled;
    const isChannelsEnabled = useFlag('SITE_CONTEXT_CHANNELS').isEnabled;

    return {
        general: true,
        audience: true,
        schema: Boolean(site.adaptiveContent?.enabled),
        domains: true,
        mcp: true,
        agent: true,
        connections: true,
        channels: isChannelsEnabled,
        embed: isEmbedAssistantPanelEnabled,
        redirects: true,
        sponsorship: isCommunity,
        legal: false,
        plan: !isCommunity,
    };
}
